import React, { useEffect, useMemo, useState } from 'react';
import { EditOutlined, SwapOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { validateShiftChange, ValidationResult } from '../../../utils/scheduleValidation';
import {
    WxbAlert,
    WxbButton,
    WxbDrawer,
    WxbSelect,
    WxbTag,
    wxbToast,
} from '../../wxb-ui';
import {
    V4ShiftAssignment,
    V4ShiftDefinition,
} from '../resultDomain';
import '../SolverV4.css';

interface ManualEditDrawerProps {
    visible: boolean;
    shiftAssignment: V4ShiftAssignment | null;
    allShiftAssignments: V4ShiftAssignment[];
    shiftOptions: V4ShiftDefinition[];
    eligibleShiftIds?: number[];
    onClose: () => void;
    onApplyEdit: (employeeId: number, date: string, newShiftId: number) => boolean;
}

const ManualEditDrawer: React.FC<ManualEditDrawerProps> = ({
    visible,
    shiftAssignment,
    allShiftAssignments,
    shiftOptions,
    eligibleShiftIds,
    onClose,
    onApplyEdit,
}) => {
    const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null);
    const [validation, setValidation] = useState<ValidationResult | null>(null);

    useEffect(() => {
        if (!shiftAssignment) return;
        setSelectedShiftId(shiftAssignment.shift_id);
        setValidation(null);
    }, [shiftAssignment]);

    useEffect(() => {
        if (!shiftAssignment || selectedShiftId === null) return;
        if (selectedShiftId === shiftAssignment.shift_id) {
            setValidation(null);
            return;
        }
        const employeeShifts = allShiftAssignments.filter(
            (assignment) => assignment.employee_id === shiftAssignment.employee_id,
        );
        setValidation(validateShiftChange(
            {
                employee_id: shiftAssignment.employee_id,
                date: shiftAssignment.date,
                shift_id: selectedShiftId,
            },
            employeeShifts,
            {
                restShiftIds: shiftOptions
                    .filter((shift) => shift.attendance_kind === 'REST')
                    .map((shift) => shift.shift_id),
            },
        ));
    }, [allShiftAssignments, selectedShiftId, shiftAssignment, shiftOptions]);

    const eligibleSet = useMemo(() => (
        eligibleShiftIds && eligibleShiftIds.length > 0
            ? new Set(eligibleShiftIds)
            : null
    ), [eligibleShiftIds]);
    const availableOptions = useMemo(() => (
        shiftOptions
            .filter((shift) => (
                shift.is_active
                && !shift.is_system_managed
                && shift.attendance_kind !== 'PAID_LEAVE'
                && shift.attendance_kind !== 'UNPAID_LEAVE'
                && (
                    shift.attendance_kind !== 'WORK'
                    || !eligibleSet
                    || eligibleSet.has(shift.shift_id)
                )
            ))
            .map((shift) => ({
                value: shift.shift_id,
                label: `${shift.shift_name}（${shift.shift_code}） · ${shift.credited_hours}h`,
            }))
    ), [eligibleSet, shiftOptions]);
    const selectedShift = useMemo(() => (
        shiftOptions.find((shift) => shift.shift_id === selectedShiftId) || null
    ), [selectedShiftId, shiftOptions]);

    if (!shiftAssignment) return null;

    const isChanged = selectedShiftId !== shiftAssignment.shift_id;
    const currentDefinition = shiftOptions.find(
        (shift) => shift.shift_id === shiftAssignment.shift_id,
    );
    const isManagedLeave = Boolean(
        currentDefinition?.is_system_managed
        || shiftAssignment.attendance_kind === 'PAID_LEAVE'
        || shiftAssignment.attendance_kind === 'UNPAID_LEAVE'
        || shiftAssignment.plan_type === 'PAID_LEAVE'
        || shiftAssignment.plan_type === 'UNPAID_LEAVE',
    );
    const isLocked = Boolean(shiftAssignment.is_locked);
    const isBlocked = isLocked
        || isManagedLeave
        || !isChanged
        || Boolean(validation && !validation.valid);

    const handleApply = () => {
        if (selectedShiftId === null || isBlocked) {
            if (validation && !validation.valid) {
                wxbToast.error('存在校验错误，无法应用');
            }
            return;
        }
        if (!onApplyEdit(shiftAssignment.employee_id, shiftAssignment.date, selectedShiftId)) return;
        wxbToast.success(`已修改 ${shiftAssignment.employee_name} 在 ${shiftAssignment.date} 的班次`);
        onClose();
    };

    return (
        <WxbDrawer
            className="v4-manual-edit-drawer"
            title={(
                <div className="v4-manual-edit-title">
                    <EditOutlined />
                    <span>手动调整班次</span>
                </div>
            )}
            open={visible}
            onClose={onClose}
            width={400}
            footer={(
                <div className="v4-manual-edit-footer">
                    <WxbButton variant="ghost" onClick={onClose}>取消</WxbButton>
                    <WxbButton variant="primary" disabled={isBlocked} onClick={handleApply}>
                        应用修改
                    </WxbButton>
                </div>
            )}
        >
            <div className="v4-manual-edit-person">
                <strong>{shiftAssignment.employee_name}</strong>
                <span>
                    {shiftAssignment.employee_code}
                    {' · '}
                    {dayjs(shiftAssignment.date).format('YYYY-MM-DD dddd')}
                </span>
            </div>

            <section className="v4-manual-edit-section">
                <h4>当前班次</h4>
                <WxbTag color="blue" className="v4-manual-edit-current-tag">
                    {shiftAssignment.shift_name}（{shiftAssignment.shift_code}）
                    {shiftAssignment.start_time
                        ? ` ${shiftAssignment.start_time}–${shiftAssignment.end_time}`
                        : ''}
                </WxbTag>
            </section>

            {isChanged && (
                <div className="v4-manual-edit-arrow" aria-hidden="true">
                    <SwapOutlined />
                </div>
            )}

            <section className="v4-manual-edit-section">
                <h4>{isChanged ? '修改为' : '选择新班次'}</h4>
                <WxbSelect
                    value={selectedShiftId}
                    options={availableOptions}
                    onChange={(value) => setSelectedShiftId(Number(value))}
                    disabled={isLocked || isManagedLeave}
                />
                {selectedShift && isChanged && (
                    <div className="v4-manual-edit-hours">
                        工时变化：{shiftAssignment.credited_hours || 0}h → {selectedShift.credited_hours}h
                        {' · '}
                        差值 {selectedShift.credited_hours - (shiftAssignment.credited_hours || 0) >= 0 ? '+' : ''}
                        {(selectedShift.credited_hours - (shiftAssignment.credited_hours || 0)).toFixed(1)}h
                    </div>
                )}
            </section>

            {isLocked && (
                <WxbAlert title="锁定班次">
                    {shiftAssignment.lock_reason || '该班次已锁定，不能在求解结果中修改。'}
                </WxbAlert>
            )}
            {isManagedLeave && (
                <WxbAlert title="系统管理班次">
                    休假班次由休假指令自动绑定，不能在结果中手工改班。
                </WxbAlert>
            )}

            {validation && (
                <div className="v4-manual-edit-validation">
                    {validation.errors.map((error, index) => (
                        <WxbAlert key={`error-${index}`} variant="error" title="不能应用">
                            {error.message}
                        </WxbAlert>
                    ))}
                    {validation.warnings.map((warning, index) => (
                        <WxbAlert key={`warning-${index}`} title="请确认">
                            {warning.message}
                        </WxbAlert>
                    ))}
                    {validation.valid && validation.errors.length === 0 && (
                        <WxbTag color="green">校验通过</WxbTag>
                    )}
                </div>
            )}
        </WxbDrawer>
    );
};

export default React.memo(ManualEditDrawer);
